import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import moment from "moment";
import React, { useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { Col, Label, Row } from "reactstrap";
import { useAnalyticsCore } from "../../cores/analytics";
import { tiktokApiService } from "../../cores/tiktok/services/api";
import { useMeCore } from "../../cores/me";
import { useRouterCore } from "../../cores/router";
import { useTiktokCore } from "../../cores/tiktok";
import { TikTokAnalyticsDashboard } from "../component/analytics/TikTokAnalyticsDashboard";
import { TikTokAnalyticsDateRangeFilter } from "../component/analytics/TikTokAnalyticsDateRangeFilter";
import { TikTokAnalyticsSnapshot } from "../component/analytics/tiktokAnalyticsMock";
import { PanelWembii } from "../component/PanelWembii";
import Content from "../layout/Content";
import Layout from "../layout/Layout";
import _ from "lodash";

const toIsoRange = (since: string, until: string): { since: string; until: string } => {
  const parse = (v: string) =>
    moment(v, ["MM/DD/YY", "MM/DD/YYYY", "YYYY-MM-DD"], true);
  const s = parse(since);
  const u = parse(until);
  if (!s.isValid() || !u.isValid()) {
    return { since, until };
  }
  return {
    since: s.format("YYYY-MM-DD"),
    until: u.format("YYYY-MM-DD"),
  };
};

export const TikTokAnalytics = () => {
  const intl = useIntl();
  const { goToSocial } = useRouterCore();
  const { date } = useAnalyticsCore();
  const {
    company,
    companyPlan,
    companyRole,
    isAdmin,
    isReseller,
  } = useMeCore();
  const { settings: tiktokSettings } = useTiktokCore();

  const isSync = useMemo(() => !_.isEmpty(tiktokSettings), [tiktokSettings]);

  const [snapshot, setSnapshot] = useState<TikTokAnalyticsSnapshot | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (
      !isSync ||
      !companyPlan?.rules.allowAnalytics ||
      (!companyRole?.analitycs &&
        !companyRole?.owner &&
        !companyRole?.admin &&
        !companyRole?.agent &&
        !isAdmin &&
        !isReseller)
    )
      goToSocial();
  }, [goToSocial, isSync, companyPlan, companyRole, isAdmin, isReseller]);

  useEffect(() => {
    if (!isSync || !company?.id) {
      return;
    }
    const { since, until } = toIsoRange(date.since, date.until);
    setLoading(true);
    setLoadError(null);
    tiktokApiService
      .getInsights(String(company.id), since, until, "Lifetime", 10)
      .then((data) => setSnapshot(data))
      .catch((err: Error & { response?: { data?: { message?: string } } }) => {
        const msg =
          err?.response?.data?.message ||
          err?.message ||
          intl.formatMessage({ id: "app.toast.error4" });
        setLoadError(typeof msg === "string" ? msg : "Error");
        setSnapshot(null);
      })
      .finally(() => setLoading(false));
  }, [company?.id, date.since, date.until, isSync, intl]);

  return (
    <Layout>
      <Content>
        <div className="tiktok-analytics-page-header tiktok-analytics-page-header--analytics">
          <div className="tiktok-analytics-page-header__toolbar">
            <div className="tiktok-analytics-page-header__lead">
              <h1 className="tiktok-analytics-page-header__title">
                {intl.formatMessage({ id: "app.tiktokAnalytics.title" })}
              </h1>
              <div
                className="tiktok-analytics-page-header__logo tiktok-analytics-page-header__logo--subtitle"
                aria-hidden
              >
                <FontAwesomeIcon icon={["fab", "tiktok"]} />
              </div>
            </div>
            <div className="tiktok-analytics-page-header__right">
              <TikTokAnalyticsDateRangeFilter />
            </div>
          </div>
        </div>
        {loading ? (
          <Row className="d-flex justify-content-center mb-3">
            <Col xs="12" md="10" lg="8">
              <PanelWembii className="mb-2">
                <div className="d-flex justify-content-center py-4 text-muted">
                  {intl.formatMessage({ id: "app.reports.updating" })}
                </div>
              </PanelWembii>
            </Col>
          </Row>
        ) : null}
        {loadError ? (
          <Row className="d-flex justify-content-center mb-3">
            <Col xs="12" md="10" lg="8">
              <PanelWembii className="mb-2">
                <Label className="text-muted text-center mb-0 w-100 py-3">
                  {loadError}
                </Label>
              </PanelWembii>
            </Col>
          </Row>
        ) : null}
        {!loading && !loadError && snapshot ? (
          <TikTokAnalyticsDashboard snapshot={snapshot} />
        ) : null}
      </Content>
    </Layout>
  );
};
